// The wheel is HONEST THEATER. The outcome is already sealed and the FS has already moved
// before a single frame animates; spinTo() just eases the wheel to the wedge the server
// already returned. There is no client-side RNG here and there must never be a fake
// near-miss — the result is pre-decided, so faking a "so close" is a literal lie.
//
// Wedges are ODDS-PROPORTIONAL (from the server's ppm weight table) so the picture matches
// the published odds — honesty, not drama. The drama the geometry could create is actively
// suppressed: tick cadence is uniform per boundary with no per-wedge special case, easing is
// identical for every outcome, and the spin duration is a compile-time constant for all
// tiers (a stopwatch-detectable tell would torch the provably-fair pitch).
//
// All randomness in this file is pure theater — landing jitter INSIDE the already-decided
// wedge, never across a boundary. It can never change which wedge the pointer reads.
//
// golden_ticket (230 ppm) gets NO wedge: a 7° floor on a 0.083°-true slice would be a 72×
// geometric lie. It lives as a hairline shimmer on the rim ("a rumor, not a target") and,
// on that outcome, the rim itself resolves the spin — an explicit branch, never the
// missing-wedge fallback.

import { tierLabel } from './copy';

const COLORS: Record<string, string> = {
  nothing: '#D2543F', // Erisian Brick — the offering / the Void
  fs_5_25: '#F5B301', // Molten Gold — FlaxScript
  spin_ticket: '#39E58C', // Arcane Green — another spin
  common_item: '#282B30', // Lit Stone — a plain trinket
  jackpot: '#FFD34E', // Bright Gold — the pot
  golden_ticket: '#F5B301', // fallback layouts only (normally rim-resolved, no wedge)
};

const STONE = '#141518';
const BORDER = '#3A3E44';
const TEXT = '#EDEDE6';
const GOLD_HI = '#FFD34E';
const GOLD_LO = '#C8860A';

// --- constants (exported for the constant-duration regression test) ---
export const SPIN_DURATION_MS = 3000; // main deceleration — identical for EVERY tier
export const SETTLE_MS = 280; // overshoot settle window (golden: bloom hold instead)
export const TOTAL_SPIN_MS = SPIN_DURATION_MS + SETTLE_MS;
export const REDUCED_TOTAL_MS = 600; // prefers-reduced-motion path (also tier-constant)
export const REVEAL_BEAT_MS = 200; // state.ts waits this after resolve before the card
export const SPIN_TURNS = 4; // constant full turns
export const MIN_ARC = (7 * Math.PI) / 180; // thin tiers stay visible
const LABEL_MIN_ARC = (18 * Math.PI) / 180; // below this, color + legend carry the meaning
const POINTER = -Math.PI / 2; // top of the canvas
const MAX_OSC_AMPLITUDE = 0.08; // radians
const OSC_CYCLES = 2;

const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);

export interface WheelEntry {
  tier: string;
  weightPpm: number;
}

export interface WedgeLayout {
  tier: string;
  start: number; // radians in wheel frame, [0, 2π), clockwise
  end: number;
}

export interface SpinPlan {
  endRotation: number;
  oscAmplitude: number; // 0 = no overshoot (golden / reduced motion)
  oscCycles: number;
  durationMs: number; // main phase
  settleMs: number;
  golden: boolean;
}

// Pure: ppm weights → proportional wedge arcs with a minimum-arc floor. golden_ticket is
// excluded (rim rumor). Throws on malformed input — the caller falls back to equal wedges
// (render must never block a spin).
export function computeLayout(entries: WheelEntry[], minArc: number = MIN_ARC): WedgeLayout[] {
  const wedged = entries.filter((e) => e.tier !== 'golden_ticket');
  if (wedged.length === 0) throw new Error('no wedge tiers');
  const total = wedged.reduce((sum, e) => {
    if (!Number.isFinite(e.weightPpm) || e.weightPpm <= 0) {
      throw new Error(`bad weight for ${e.tier}`);
    }
    return sum + e.weightPpm;
  }, 0);

  // Floor thin arcs, take the deficit proportionally from the un-floored ones. The floor
  // can cascade, so iterate with a MONOTONE floored set (an arc pinned to the floor never
  // rejoins the free pool — otherwise near-floor tables oscillate and can exit the loop
  // slightly under the floor). Free arcs always share the remaining budget by their
  // ORIGINAL fractions, so every non-floored arc ends ≥ minArc by construction.
  const fractions = wedged.map((e) => e.weightPpm / total);
  const floored = new Set<number>();
  let arcs: number[] = [];
  for (let pass = 0; pass <= wedged.length; pass++) {
    const budget = Math.PI * 2 - floored.size * minArc;
    const freeFraction = fractions.reduce((s, f, i) => (floored.has(i) ? s : s + f), 0);
    if (budget <= 0 || (freeFraction <= 0 && floored.size < wedged.length)) {
      throw new Error('min-arc floor infeasible');
    }
    arcs = fractions.map((f, i) => (floored.has(i) ? minArc : (f / freeFraction) * budget));
    const before = floored.size;
    arcs.forEach((a, i) => {
      if (!floored.has(i) && a < minArc - 1e-12) floored.add(i);
    });
    if (floored.size === before) break;
  }

  const layout: WedgeLayout[] = [];
  let cursor = 0;
  for (let i = 0; i < wedged.length; i++) {
    layout.push({ tier: wedged[i].tier, start: cursor, end: cursor + arcs[i] });
    cursor += arcs[i];
  }
  // Absorb float dust so the last edge meets 2π exactly.
  layout[layout.length - 1].end = Math.PI * 2;
  return layout;
}

// Pure: where and how the spin ends. `rand` ∈ [0,1) drives only the in-wedge landing jitter
// (≤35% of the half-arc — always safely inside the decided wedge). Duration is constant
// across all tiers by construction; the regression test asserts it.
export function planSpin(
  layout: WedgeLayout[],
  targetTier: string,
  currentRotation: number,
  rand: () => number,
  reducedMotion = false,
): SpinPlan {
  const durationMs = reducedMotion ? REDUCED_TOTAL_MS : SPIN_DURATION_MS;
  const settleMs = reducedMotion ? 0 : SETTLE_MS;
  const wedge = layout.find((w) => w.tier === targetTier);

  if (!wedge) {
    // golden_ticket (rim-resolved) — or, defensively, an unknown tier: free-stop after the
    // same constant turns/duration; the rim bloom (golden) or the result card carries the
    // truth. No oscillation: the stop is held still for the settle window.
    return {
      endRotation: currentRotation + Math.PI * 2 * SPIN_TURNS,
      oscAmplitude: 0,
      oscCycles: 0,
      durationMs,
      settleMs,
      golden: targetTier === 'golden_ticket',
    };
  }

  const halfArc = (wedge.end - wedge.start) / 2;
  const center = (wedge.start + wedge.end) / 2;
  const jitter = (rand() * 2 - 1) * 0.35 * halfArc;
  const landing = center + jitter;
  // Pointer reads wheel-frame angle a when rotation ≡ −a (mod 2π).
  const endRotation = normalizeToward(currentRotation, -landing) + Math.PI * 2 * SPIN_TURNS;
  const boundaryDistance = Math.min(landing - wedge.start, wedge.end - landing);
  const oscAmplitude = reducedMotion
    ? 0
    : Math.min(MAX_OSC_AMPLITUDE, 0.8 * boundaryDistance);
  return {
    endRotation,
    oscAmplitude,
    oscCycles: reducedMotion ? 0 : OSC_CYCLES,
    durationMs,
    settleMs,
    golden: targetTier === 'golden_ticket',
  };
}

// Pure: boundaries swept past the pointer while rotation moves r1 → r2 (r2 ≥ r1). Exact for
// unequal wedges: boundary b sits under the pointer whenever rotation ≡ −b (mod 2π).
export function countBoundariesCrossed(
  boundaries: number[],
  r1: number,
  r2: number,
): number {
  const twoPi = Math.PI * 2;
  let crossed = 0;
  for (const b of boundaries) {
    crossed += Math.floor((r2 + b) / twoPi) - Math.floor((r1 + b) / twoPi);
  }
  return crossed;
}

export interface SpinOptions {
  // Uniform per-boundary tick: `crossed` boundaries swept this frame at `velocity` rad/s.
  // NO per-wedge information is exposed — cadence and pitch derive from geometry alone.
  onTick?: (crossed: number, velocity: number) => void;
}

export class Wheel {
  private ctx: CanvasRenderingContext2D;
  private rotation = 0; // radians
  private layout: WedgeLayout[] = [];
  private boundaries: number[] = [];
  private spinning = false;
  private goldenPhase = 0; // 0 = normal · 1 = full rim bloom (golden finale)
  private pre: HTMLCanvasElement | null = null;
  private size = 360; // device-pixel backing size
  private readonly onResize = () => this.resize();

  constructor(
    private canvas: HTMLCanvasElement,
    entries: WheelEntry[],
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    this.ctx = ctx;
    this.setLayout(entries);
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize);
      window.addEventListener('orientationchange', this.onResize);
    }
  }

  destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
    }
  }

  // Per-mode relayout (paid and free tables differ). Falls back to equal wedges over ALL
  // given tiers (including golden_ticket, which then owns a real wedge and is spun to
  // normally) if the ppm data is malformed.
  //
  // While a spin is in flight the relayout is DEFERRED: the running plan's endRotation was
  // computed against the current geometry, and swapping wedge boundaries under it would let
  // the pointer come to rest on a wedge that is not the settled tier. The pending layout
  // applies the moment the spin resolves. (state.ts also gates mode switches while busy —
  // this is the belt-and-suspenders.)
  private pendingEntries: WheelEntry[] | null = null;

  setLayout(entries: WheelEntry[]): void {
    if (this.spinning) {
      this.pendingEntries = entries;
      return;
    }
    try {
      this.layout = computeLayout(entries);
    } catch {
      const tiers = entries.length ? entries.map((e) => e.tier) : ['nothing'];
      const arc = (Math.PI * 2) / tiers.length;
      this.layout = tiers.map((tier, i) => ({ tier, start: i * arc, end: (i + 1) * arc }));
    }
    this.boundaries = this.layout.map((w) => w.start);
    this.resize(); // prerenders + draws
  }

  hasWedge(tier: string): boolean {
    return this.layout.some((w) => w.tier === tier);
  }

  // DPR-aware backing store sized from the CSS box (styles.css gives the canvas
  // width:min(360px,100%) + aspect-ratio:1). Blurry-phone fix.
  private resize(): void {
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const rect = this.canvas.getBoundingClientRect();
    const css = rect.width > 0 ? rect.width : 360;
    const size = Math.max(1, Math.round(css * dpr));
    if (size !== this.size || this.canvas.width !== size) {
      this.size = size;
      this.canvas.width = size;
      this.canvas.height = size;
    }
    this.prerender();
    this.draw();
  }

  // Static wheel image (wedges + labels + rim rumor) drawn once per layout/size; each frame
  // is then clear + rotate + drawImage — the perf backbone for the Discord iframe.
  private prerender(): void {
    const size = this.size;
    const pre = document.createElement('canvas');
    pre.width = size;
    pre.height = size;
    const ctx = pre.getContext('2d');
    if (!ctx) return;
    const c = size / 2;
    const r = size / 2 - Math.max(8, size * 0.022);
    const scale = size / 360; // typography tracks the backing size

    for (const wedge of this.layout) {
      const start = wedge.start + POINTER;
      const end = wedge.end + POINTER;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.arc(c, c, r, start, end);
      ctx.closePath();
      ctx.fillStyle = COLORS[wedge.tier] ?? '#1E2024';
      ctx.fill();
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 2 * scale;
      ctx.stroke();

      if (wedge.end - wedge.start >= LABEL_MIN_ARC) {
        ctx.save();
        ctx.translate(c, c);
        ctx.rotate((wedge.start + wedge.end) / 2 + POINTER);
        ctx.textAlign = 'right';
        ctx.fillStyle = wedge.tier === 'common_item' ? TEXT : STONE;
        ctx.font = `600 ${Math.round(13 * scale)}px system-ui, sans-serif`;
        ctx.fillText(tierLabel(wedge.tier), r - 12 * scale, 5 * scale);
        ctx.restore();
      }
    }

    // The rim: a quiet gold ring, plus one brighter hairline segment — the golden rumor.
    // It rotates with the wheel and points at nothing; kallisti.
    ctx.beginPath();
    ctx.arc(c, c, r + 3 * scale, 0, Math.PI * 2);
    ctx.strokeStyle = GOLD_LO;
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = 1.5 * scale;
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(c, c, r + 3 * scale, 0.9, 0.95);
    ctx.strokeStyle = GOLD_HI;
    ctx.lineWidth = 2 * scale;
    ctx.stroke();

    this.pre = pre;
  }

  private draw(): void {
    const { ctx } = this;
    const size = this.size;
    const c = size / 2;
    const r = size / 2 - Math.max(8, size * 0.022);
    const scale = size / 360;
    ctx.clearRect(0, 0, size, size);

    if (this.pre) {
      ctx.save();
      ctx.translate(c, c);
      ctx.rotate(this.rotation);
      ctx.drawImage(this.pre, -c, -c);
      ctx.restore();
    }

    // Golden finale: the wedges recede, the rim carries the answer.
    if (this.goldenPhase > 0) {
      ctx.save();
      ctx.globalAlpha = 0.72 * this.goldenPhase;
      ctx.beginPath();
      ctx.arc(c, c, r + 1 * scale, 0, Math.PI * 2);
      ctx.fillStyle = STONE;
      ctx.fill();
      ctx.globalAlpha = this.goldenPhase;
      ctx.beginPath();
      ctx.arc(c, c, r + 3 * scale, 0, Math.PI * 2);
      ctx.strokeStyle = GOLD_HI;
      ctx.lineWidth = (2 + 6 * this.goldenPhase) * scale;
      ctx.stroke();
      ctx.restore();
    }

    // Hub.
    ctx.beginPath();
    ctx.arc(c, c, 18 * scale, 0, Math.PI * 2);
    ctx.fillStyle = STONE;
    ctx.fill();
    ctx.strokeStyle = '#F5B301';
    ctx.lineWidth = 2 * scale;
    ctx.stroke();

    // Pointer at top.
    ctx.beginPath();
    ctx.moveTo(c - 10 * scale, c - r - 2 * scale);
    ctx.lineTo(c + 10 * scale, c - r - 2 * scale);
    ctx.lineTo(c, c - r + 14 * scale);
    ctx.closePath();
    ctx.fillStyle = '#F5B301';
    ctx.fill();
  }

  // Ease the wheel to the server-returned tier. Constant duration for every tier; uniform
  // boundary ticks; damped in-wedge overshoot; golden_ticket resolves on the rim.
  spinTo(targetTier: string, opts: SpinOptions = {}): Promise<void> {
    if (this.spinning) return Promise.resolve();
    // A relayout deferred during the previous spin applies now, BEFORE this spin plans —
    // never at spin-stop, where it would change what the resting pointer reads mid-reveal.
    if (this.pendingEntries) {
      const entries = this.pendingEntries;
      this.pendingEntries = null;
      this.setLayout(entries);
    }
    this.spinning = true;
    this.goldenPhase = 0;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    const plan = planSpin(this.layout, targetTier, this.rotation, Math.random, reduced);
    if (targetTier !== 'golden_ticket' && !this.hasWedge(targetTier)) {
      // Defensive only — every reachable outcome tier is mounted per mode. Log loudly; the
      // free-stop plan above keeps the theater from contradicting the sealed result.
      console.error(`[wheel] outcome tier "${targetTier}" has no wedge; check the mount set`);
    }

    const start = this.rotation;
    const delta = plan.endRotation - start;

    return new Promise<void>((resolve) => {
      const startTs = performance.now();
      let prevRotation = start;
      let prevTs = startTs;

      const frame = (now: number) => {
        // Floor at 0: the first rAF timestamp (vsync start) can precede the
        // performance.now() captured above, and a negative t would extrapolate the
        // easing into a one-frame backward kick with duplicated ticks.
        const elapsed = Math.max(0, now - startTs);
        const t = Math.min(1, elapsed / plan.durationMs);
        if (t < 1) {
          this.rotation = start + delta * easeOutQuart(t);
        } else {
          // Settle window: damped overshoot around the landing (or, golden, a held stop
          // while the rim blooms). Amplitude is clamped below the boundary distance, so the
          // pointer can never read a neighboring wedge — not even for a frame.
          const s = Math.min(1, (elapsed - plan.durationMs) / Math.max(1, plan.settleMs));
          const damped =
            plan.oscAmplitude *
            Math.sin(s * plan.oscCycles * Math.PI * 2) *
            (1 - s);
          this.rotation = plan.endRotation + damped;
        }

        if (plan.golden) {
          const bloomStart = 0.6;
          const total = plan.durationMs + plan.settleMs;
          this.goldenPhase = Math.max(0, Math.min(1, (elapsed / total - bloomStart) / (1 - bloomStart)));
        }

        const dtSeconds = Math.max(1e-6, (now - prevTs) / 1000);
        const crossed = countBoundariesCrossed(this.boundaries, prevRotation, this.rotation);
        if (crossed > 0) opts.onTick?.(crossed, (this.rotation - prevRotation) / dtSeconds);
        prevRotation = this.rotation;
        prevTs = now;

        this.draw();

        if (elapsed < plan.durationMs + plan.settleMs) {
          requestAnimationFrame(frame);
        } else {
          const twoPi = Math.PI * 2;
          this.rotation = ((plan.endRotation % twoPi) + twoPi) % twoPi;
          this.draw();
          this.spinning = false;
          resolve();
        }
      };
      requestAnimationFrame(frame);
    });
  }

  clearGolden(): void {
    this.goldenPhase = 0;
    this.draw();
  }
}

// Rotate `target` forward relative to `from` so we always ease clockwise-forward, never snap.
function normalizeToward(from: number, target: number): number {
  const twoPi = Math.PI * 2;
  const base = Math.floor(from / twoPi) * twoPi;
  let candidate = base + (((target % twoPi) + twoPi) % twoPi);
  while (candidate < from) candidate += twoPi;
  return candidate;
}
